/**
 * KASHEER walking deer
 * The brand deer crosses the header, left to right. It walks along the
 * bottom edge (under the icons), now and then breaks into a short gallop or
 * a leap, and jumps up onto the name: it lands on the letters, pauses, walks
 * along them, then hops back down to the edge. It walks off the right and
 * comes back from the left a little later.
 *
 * Drawn in the reference artwork's 37 x 88 units (hoofs at y 88). It is
 * scaled to fit in the bar standing on the name and to pass under the icons.
 * Each leg is a two-bone limb solved every frame, so a hoof stays planted
 * while the body passes over it; the stride is driven by distance covered.
 *
 * Markup (sections/header.liquid): <div class="kasheer-deer" data-kasheer-deer aria-hidden="true"></div>
 * Reduced motion: the deer stands still at the left.
 */
(function () {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';
  var GROUND = 88;
  var ENTER_AT = -60;         // units off the left edge where each crossing starts
  var HIND_TOE = 1.8;         // hind hoof, units from the deer's left edge
  var FORE_TOE = 31;          // front hoof
  var MIN_PLATFORM = 38;      // narrowest icon (units) it will stand on
  var TAKEOFF = 26;           // units between the front hoof and an icon at take-off
  var HOP_REACH = 120;        // widest gap (units) it hops across from icon to icon
  var APEX = 7;               // units the arc rises above the higher end of a jump

  // Gaits. stance: share of a stride a hoof is down; step: how far a planted
  // hoof travels under the body; lift: hoof height mid-swing; speed: units/s.
  var WALK = { stance: 0.65, step: 14, lift: 3.4, speed: 30 };
  var RUN = { stance: 0.32, step: 13, lift: 6.5, speed: 110 };
  var HEAD_DOWN = 16;         // degrees the head drops while it stands

  // walk: lateral sequence (hind, same-side fore, other pair). run: rotary gallop.
  var LEGS = [
    { hip: [5, 54], rest: -4.4, a: 16, b: 18.6, hind: true, far: true, walk: 0.5, run: 0.12 },
    { hip: [27, 57.5], rest: 0.6, a: 15.35, b: 15.35, far: true, walk: 0.75, run: 0.5 },
    { hip: [6.2, 54], rest: -4.4, a: 16, b: 18.6, hind: true, walk: 0, run: 0 },
    { hip: [29.6, 57.5], rest: 1.4, a: 15.35, b: 15.35, walk: 0.25, run: 0.62 }
  ];

  var BODY = 'M1 47.5H32.4V58.8H1Z';
  var NECK = 'M28.1 50L29.1 28.6L30.7 28.6L32.4 50Z';
  var HEAD = 'M29.1 29L28.9 26.6L30.1 25.9L35.2 29L31.1 30.2L30.6 29.4Z' +
    'M29.4 26.8L27.6 25.3L29.9 25.9Z';
  var ANTLERS = 'M28.7 25.8L26.6 18.2L24.9 11.2L23.7 4.4L23.3 1.2' +
    'M26.6 18.2L22.6 16.6M24.9 11.2L21.1 9.9M24.9 11.2L27.4 6.8M23.7 4.4L20.7 3.4M23.7 4.4L26.3 1.6' +
    'M29.7 25.8L31.7 18.6L33.3 12.6L34.7 8.1' +
    'M31.7 18.6L35.2 16.8M33.3 12.6L30.9 10.1M34.7 8.1L36.2 5.9M34.7 8.1L32.9 4.7';
  var NECK_PIVOT = [30.3, 50];
  var BODY_CENTRE = [17, 53];

  function el(name, attrs, parent) {
    var node = document.createElementNS(NS, name);
    Object.keys(attrs).forEach(function (key) { node.setAttribute(key, attrs[key]); });
    if (parent) parent.appendChild(node);
    return node;
  }

  function clamp(value, min, max) { return Math.min(max, Math.max(min, value)); }
  function frac(value) { return value - Math.floor(value); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function smooth(t) { return t * t * (3 - 2 * t); }
  function approach(value, target, dt, tau) { return value + (target - value) * (1 - Math.exp(-dt / tau)); }
  function rand(min, max) { return min + Math.random() * (max - min); }
  function fixed(pt) { return pt[0].toFixed(2) + ',' + pt[1].toFixed(2); }

  function rotate(pt, centre, degrees) {
    var r = degrees * Math.PI / 180;
    var dx = pt[0] - centre[0];
    var dy = pt[1] - centre[1];
    return [centre[0] + dx * Math.cos(r) - dy * Math.sin(r), centre[1] + dx * Math.sin(r) + dy * Math.cos(r)];
  }

  // Two-bone solve: the joint sits on the side given by `side` (+1 behind for hocks, -1 ahead for knees).
  function solve(hip, foot, a, b, side) {
    var dx = foot[0] - hip[0];
    var dy = foot[1] - hip[1];
    var d = clamp(Math.sqrt(dx * dx + dy * dy), Math.abs(a - b) + 0.01, a + b - 0.01);
    var aim = Math.atan2(dy, dx);
    var spread = Math.acos(clamp((a * a + d * d - b * b) / (2 * a * d), -1, 1));
    var angle = aim + side * spread;
    return [hip[0] + a * Math.cos(angle), hip[1] + a * Math.sin(angle)];
  }

  // Where a hoof sits relative to its hip for a point in the stride (0..1).
  function hoof(p, gait) {
    if (p < gait.stance) return [gait.step / 2 - (p / gait.stance) * gait.step, 0];
    var q = (p - gait.stance) / (1 - gait.stance);
    return [-gait.step / 2 + smooth(q) * gait.step, -gait.lift * Math.sin(Math.PI * q)];
  }

  // Hoof targets relative to the hip while airborne: forelegs fold then reach, hind legs push then gather.
  function airHoof(spec, t) {
    if (spec.hind) {
      var push = smooth(clamp(t / 0.85, 0, 1));
      return [lerp(-18, 0, push), lerp(23, 24, push)];
    }
    var reach = smooth(clamp((t - 0.45) / 0.55, 0, 1));
    return [lerp(-2, 8, reach), lerp(15, 29, reach)];
  }

  function haunch(hip, hock) {
    var dx = hock[0] - hip[0];
    var dy = hock[1] - hip[1];
    var len = Math.sqrt(dx * dx + dy * dy) || 1;
    var nx = -dy / len;
    var ny = dx / len;
    return [
      [hip[0] + nx * 4.6, hip[1] + ny * 4.6],
      [hock[0] + nx * 0.85, hock[1] + ny * 0.85],
      [hock[0] - nx * 0.85, hock[1] - ny * 0.85],
      [hip[0] - nx * 4.6, hip[1] - ny * 4.6]
    ].map(fixed).join(' ');
  }

  function build(host) {
    var svg = el('svg', { class: 'kasheer-deer__svg', focusable: 'false' }, host);
    var deer = el('g', { class: 'kasheer-deer__figure' }, svg);
    var legs = LEGS.map(function (spec) {
      var group = el('g', { class: spec.far ? 'kasheer-deer__leg is-far' : 'kasheer-deer__leg' });
      return {
        spec: spec,
        group: group,
        upper: spec.hind ? el('polygon', {}, group) : el('polyline', { class: 'kasheer-deer__bone' }, group),
        lower: spec.hind ? el('line', { class: 'kasheer-deer__bone' }, group) : null
      };
    });
    legs.filter(function (leg) { return leg.spec.far; }).forEach(function (leg) { deer.appendChild(leg.group); });
    var body = el('g', {}, deer);
    el('path', { d: BODY }, body);
    var head = el('g', {}, body);
    el('path', { d: NECK }, head);
    el('path', { d: HEAD }, head);
    el('path', { d: ANTLERS, class: 'kasheer-deer__antlers' }, head);
    legs.filter(function (leg) { return !leg.spec.far; }).forEach(function (leg) { deer.appendChild(leg.group); });
    return { svg: svg, deer: deer, body: body, head: head, legs: legs };
  }

  function drawLeg(leg, hip, foot) {
    var spec = leg.spec;
    var joint = solve(hip, foot, spec.a, spec.b, spec.hind ? 1 : -1);
    if (spec.hind) {
      leg.upper.setAttribute('points', haunch(hip, joint));
      leg.lower.setAttribute('x1', joint[0].toFixed(2));
      leg.lower.setAttribute('y1', joint[1].toFixed(2));
      leg.lower.setAttribute('x2', foot[0].toFixed(2));
      leg.lower.setAttribute('y2', foot[1].toFixed(2));
    } else {
      leg.upper.setAttribute('points', [hip, joint, foot].map(fixed).join(' '));
    }
  }

  /**
   * Draw one frame.
   * @param {object} parts  SVG nodes from build()
   * @param {{walkPhase:number, runPhase:number, walk:number, run:number, head:number,
   *          floor:number, lift:number, air:number, airT:number}} m
   *   floor: hoof level (units, GROUND = the bar's edge); lift: body height above it (negative = up);
   *   air: 0 on the ground to 1 mid-jump; airT: progress through the jump (0..1).
   */
  function pose(parts, m) {
    var walkBob = -m.walk * 0.35 * (1 + Math.cos(4 * Math.PI * m.walkPhase));
    var runBob = -m.run * 1.6 * (0.5 - 0.5 * Math.cos(2 * Math.PI * m.runPhase));
    var pitch = m.run * 3 * Math.sin(2 * Math.PI * m.runPhase) - 8 * Math.cos(Math.PI * m.airT) * m.air;
    var rise = m.floor - GROUND + m.lift;
    var dy = lerp(walkBob, runBob, m.run) * (1 - m.air) + rise;
    var nod = m.walk * (1 - m.run) * (1 - m.air) * 2 * Math.sin(2 * Math.PI * (2 * m.walkPhase + 0.1));

    parts.body.setAttribute('transform', 'translate(0 ' + dy.toFixed(3) + ') rotate(' + pitch.toFixed(2) + ' ' + BODY_CENTRE[0] + ' ' + BODY_CENTRE[1] + ')');
    parts.head.setAttribute('transform', 'rotate(' + (Math.max(m.head, 14 * m.air) + nod).toFixed(2) + ' ' + NECK_PIVOT[0] + ' ' + NECK_PIVOT[1] + ')');

    parts.legs.forEach(function (leg) {
      var spec = leg.spec;
      var hip = rotate(spec.hip, BODY_CENTRE, pitch);
      hip = [hip[0], hip[1] + dy];
      var walkStride = hoof(frac(m.walkPhase + spec.walk), WALK);
      var runStride = hoof(frac(m.runPhase + spec.run), RUN);
      var sx = lerp(m.walk * walkStride[0], runStride[0], m.run);
      var sy = lerp(m.walk * walkStride[1], runStride[1], m.run);
      var foot = [spec.hip[0] + spec.rest + sx, m.floor + sy];
      if (m.air > 0) {
        var tuck = airHoof(spec, m.airT);
        foot = [lerp(foot[0], hip[0] + tuck[0], m.air), lerp(foot[1], hip[1] + tuck[1], m.air)];
      }
      drawLeg(leg, hip, foot);
    });
  }

  // The icons and the name, as boxes in pixels from the bar's top-left. Text is
  // measured by its glyph box and trimmed to the capitals, so the deer stands on the letters.
  // `brand` marks the name (or logo image), the only thing it jumps onto.
  function readIcons(host) {
    var hostBox = host.getBoundingClientRect();
    var nodes = host.parentElement.querySelectorAll('.kasheer-header__inner svg, .kasheer-header__inner img, .kasheer-header__brand-latin, .kasheer-header__country button');
    var boxes = [];
    Array.prototype.forEach.call(nodes, function (node) {
      var box = node.getBoundingClientRect();
      var trim = 0.06;
      if (node.tagName !== 'svg' && node.tagName !== 'IMG') {
        var range = document.createRange();
        range.selectNodeContents(node);
        box = range.getBoundingClientRect();
        trim = 0.2;
      }
      if (box.width < 1 || box.height < 1) return;
      boxes.push({
        left: box.left - hostBox.left,
        right: box.right - hostBox.left,
        top: box.top - hostBox.top + box.height * trim,
        bottom: box.bottom - hostBox.top,
        brand: node.closest('.kasheer-header__brand') !== null
      });
    });
    return boxes.sort(function (a, b) { return a.left - b.left; });
  }

  // Jump arc: rises to just above the higher end, then drops onto the landing.
  function arcHeight(jump, t) {
    if (t < jump.peakAt) {
      var u = t / jump.peakAt;
      return jump.y0 + (jump.peak - jump.y0) * (1 - (1 - u) * (1 - u));
    }
    var v = (t - jump.peakAt) / (1 - jump.peakAt);
    return jump.peak + (jump.y1 - jump.peak) * v * v;
  }

  function createDeer(host) {
    var parts = build(host);
    var s = {
      x: ENTER_AT, y: GROUND, speed: 0, target: 0, walkPhase: 0, runPhase: 0, run: 0,
      head: 0, headTarget: 0, jump: null, on: null, leapCooldown: 3,
      mode: 'wait', timer: 1.2, stood: false,
      scale: 0.5, width: 0, height: 0, platforms: [], icons: [], age: 0
    };

    function measure() {
      s.width = host.clientWidth;
      s.height = host.clientHeight;
      var icons = readIcons(host);
      // Two thirds of the bar, but small enough to fit standing on the highest icon,
      // which also keeps it under the icons while it walks the edge.
      var highest = icons.length ? Math.min.apply(null, icons.map(function (b) { return b.top; })) : s.height;
      var lowest = icons.length ? Math.max.apply(null, icons.map(function (b) { return b.bottom; })) : 0;
      s.scale = Math.min((s.height * 0.66) / GROUND, (highest - 1) / (GROUND + APEX), (s.height - lowest - 2) / GROUND);
      parts.svg.setAttribute('viewBox', '0 0 ' + s.width + ' ' + s.height);
      // Hairline legs stay at least 1px wide at any size.
      host.style.setProperty('--kasheer-deer-line', Math.max(1.4, 1 / s.scale).toFixed(2));
      s.icons = icons.map(function (b) {
        return { left: b.left / s.scale, right: b.right / s.scale, top: GROUND - (s.height - b.top) / s.scale, brand: b.brand };
      });
      s.platforms = s.icons.filter(function (p) { return p.brand && p.right - p.left >= MIN_PLATFORM; });
      s.age = 0;
      if (s.on) {
        // Keep standing on the same icon after a re-measure; if it has gone, hop down.
        var feet = s.x + (HIND_TOE + FORE_TOE) / 2;
        s.on = s.platforms.filter(function (p) { return p.left <= feet && p.right >= feet; })[0] || null;
        if (s.on) s.y = s.on.top;
        else if (!s.jump) startJump(null, s.x + 6);
      }
    }

    function place() {
      parts.deer.setAttribute('transform', 'translate(' + (s.x * s.scale).toFixed(2) + ' ' + s.height +
        ') scale(' + s.scale.toFixed(4) + ') translate(0 ' + -GROUND + ')');
    }

    // Jump to a platform (landing at its left end) or, with `to` null, down to the edge at x1.
    function startJump(to, x1) {
      var y1 = to ? to.top : GROUND;
      var up = y1 < s.y - 1;
      var down = y1 > s.y + 1;
      s.jump = {
        to: to, from: s.on, t: 0, x0: s.x, x1: to ? to.left + 1.5 : x1, y0: s.y, y1: y1,
        peak: Math.min(s.y, y1) - (to || s.on ? APEX : 14),
        peakAt: up ? 0.68 : (down ? 0.3 : 0.5),
        time: up ? 0.75 : 0.58
      };
      s.on = null;
    }

    function land() {
      var jump = s.jump;
      s.jump = null;
      s.x = jump.x1;
      s.y = jump.y1;
      s.on = jump.to;
      if (s.on) setMode('perch');
      else if (jump.from) { s.speed = Math.min(s.speed, WALK.speed); setMode('walk'); }
      else s.speed = s.target; // a leap in the open keeps its gait
    }

    function setMode(mode) {
      s.mode = mode;
      if (mode === 'walk') { s.target = s.on ? WALK.speed * 0.7 : WALK.speed; s.timer = rand(2, 4.5); }
      if (mode === 'run') { s.target = RUN.speed; s.timer = rand(2, 4); s.leapCooldown = rand(0.4, 1); }
      if (mode === 'stand') { s.target = 0; s.timer = rand(3, 5); s.stood = true; }
      if (mode === 'perch') { s.target = 0; s.speed = 0; s.timer = rand(0.9, 2); }
      if (mode === 'wait') {
        s.target = 0; s.speed = 0; s.timer = rand(4, 8); s.x = ENTER_AT; s.y = GROUND; s.on = null; s.stood = false;
      }
    }

    function nextMode() {
      var span = s.width / s.scale;
      var middle = s.x > span * 0.25 && s.x < span * 0.7;
      var roll = Math.random();
      if (s.mode === 'run' || s.on) return setMode('walk');
      if (!s.stood && middle && roll < 0.3) return setMode('stand');
      setMode(roll < 0.75 ? 'run' : 'walk');
    }

    function nextPlatform(after) {
      return s.platforms.filter(function (p) { return p.left >= after; })[0] || null;
    }

    // On the edge: jump up onto the name when it is close; leap now and then where no icon is overhead.
    function groundMoves(dt) {
      var ahead = nextPlatform(s.x + FORE_TOE - 2);
      if (ahead && s.x + FORE_TOE + TAKEOFF >= ahead.left) return startJump(ahead);
      s.leapCooldown -= dt;
      var chance = s.mode === 'run' ? 1 : (s.mode === 'walk' ? dt * 0.08 : 0);
      if (s.leapCooldown > 0 || Math.random() >= chance) return;
      var reach = s.x + Math.max(s.speed, 60) * 0.58;
      if (ahead && reach + FORE_TOE + TAKEOFF >= ahead.left) return;
      var near = s.icons.some(function (o) { return o.right >= s.x - 4 && o.left <= reach + FORE_TOE + 8; });
      if (near) return; // never leap up into an icon
      s.leapCooldown = s.mode === 'run' ? rand(0.9, 2) : rand(4, 8);
      startJump(null, reach);
    }

    // On an icon: at its end, hop across to the next icon if it is near, else down to the edge.
    function platformMoves() {
      if (s.x + FORE_TOE < s.on.right - 1) return;
      var next = nextPlatform(s.on.right);
      if (next && next.left - s.on.right <= HOP_REACH) return startJump(next);
      startJump(null, s.on.right + 2);
    }

    function behave(dt) {
      s.timer -= dt;
      if (s.mode === 'wait') { if (s.timer <= 0) setMode('walk'); return; }
      if (s.mode === 'perch') {
        s.headTarget = s.timer > 0.6 ? HEAD_DOWN * 0.6 : 0;
        if (s.timer <= 0) { s.headTarget = 0; setMode('walk'); }
        return;
      }
      if (s.mode === 'stand') {
        if (s.speed < 0.6) s.headTarget = s.timer > 1.4 ? HEAD_DOWN : 0;
        else s.timer += dt; // the stand starts once it has come to rest
        if (s.timer <= 0) { s.headTarget = 0; setMode('walk'); }
        return;
      }
      if (s.timer <= 0) nextMode();
    }

    function tick(dt) {
      var span = s.width / s.scale;
      s.age += dt;
      if (s.age > 2) measure();
      var x0 = s.x;
      var air = 0;
      var airT = 0;
      var lift = 0;
      var floor = s.y;

      if (s.jump) {
        var jump = s.jump;
        jump.t = Math.min(1, jump.t + dt / jump.time);
        s.x = lerp(jump.x0, jump.x1, jump.t);
        floor = lerp(jump.y0, jump.y1, smooth(jump.t));
        lift = arcHeight(jump, jump.t) - floor;
        air = Math.pow(Math.sin(Math.PI * jump.t), 0.6);
        airT = jump.t;
        s.speed = Math.abs(jump.x1 - jump.x0) / jump.time;
        if (jump.t >= 1) { land(); floor = s.y; lift = 0; air = 0; }
      } else {
        behave(dt);
        s.speed = approach(s.speed, s.target, dt, s.target > s.speed ? 0.55 : 0.75);
        s.x += s.speed * dt;
        if (s.mode !== 'wait' && s.mode !== 'perch') {
          if (s.on) platformMoves();
          else groundMoves(dt);
        }
      }

      var dx = s.x - x0;
      s.walkPhase += dx / (WALK.step / WALK.stance);
      s.runPhase += dx / (RUN.step / RUN.stance);
      s.run = approach(s.run, s.mode === 'run' && !s.on ? 1 : 0, dt, 0.4);
      s.head = approach(s.head, s.headTarget, dt, 0.9);

      if (s.x > span + 4) setMode('wait');
      place();
      pose(parts, {
        walkPhase: s.walkPhase, runPhase: s.runPhase, run: s.run, head: s.head,
        walk: s.jump ? 1 : clamp(s.speed / WALK.speed, 0, 1),
        floor: floor, lift: lift, air: air, airT: airT
      });
    }

    return { state: s, measure: measure, place: place, tick: tick, parts: parts };
  }

  function init(host) {
    if (host.dataset.kasheerDeerReady) return;
    host.dataset.kasheerDeerReady = 'true';
    var deer = createDeer(host);
    var s = deer.state;
    deer.measure();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      s.x = 24;
      deer.place();
      pose(deer.parts, { walkPhase: 0, runPhase: 0, walk: 0, run: 0, head: 0, floor: GROUND, lift: 0, air: 0, airT: 0 });
      return;
    }

    var last = null;
    function frame(now) {
      var dt = last === null ? 0 : Math.min(0.05, (now - last) / 1000);
      last = now;
      deer.tick(dt);
      window.requestAnimationFrame(frame);
    }
    deer.place();
    window.requestAnimationFrame(frame);
    window.addEventListener('resize', function () { deer.measure(); deer.place(); });
    document.addEventListener('visibilitychange', function () { last = null; });
  }

  function start() {
    document.querySelectorAll('[data-kasheer-deer]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
